import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { API_BASE } from "@/lib/api";

const REFERRAL_LS_KEY = "runidea_share_token";

export default function Signup() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");
  const [shareToken, setShareToken] = useState(null);
  const { signup } = useAuth();
  const navigate = useNavigate();

  // Phase 23 — pull a referral share_token out of localStorage (set by the
  // Landing page when the URL contained `?utm_source=referral&share_token=X`).
  // Batch AV — categorised as PREFERENCE. The `share_token` is a
  // marketing attribution identifier (analogous to a UTM parameter),
  // NOT an auth token. It carries no PII and its only effect is to
  // credit the referring user with a successful signup. Reading it
  // via XSS would only reveal that the visitor came from a referral
  // link — no security boundary crossed. OK to keep in localStorage.
  useEffect(() => {
    try {
      const t = window.localStorage.getItem(REFERRAL_LS_KEY);
      if (t) setShareToken(t);
    } catch (e) {
      // Phase 36 Batch 1 — log instead of silent swallow. localStorage
      // is disabled in private browsing modes; signup still works,
      // referral attribution just won't apply.
      console.warn("[Signup] read referral token failed:", e);
    }
  }, []);

  const onSubmit = async (ev) => {
    ev.preventDefault();
    setErr("");
    if (password.length < 8) {
      setErr("Password must be at least 8 characters.");
      return;
    }
    setSubmitting(true);
    try {
      await signup(name.trim(), email.trim().toLowerCase(), password, shareToken);
      // Clear the referral token after a successful attribution so the next
      // visitor on this device doesn't inadvertently re-use it.
      try { window.localStorage.removeItem(REFERRAL_LS_KEY); } catch (e) {
        // Phase 36 Batch 1 — log instead of silent swallow.
        console.warn("[Signup] clear referral token failed:", e);
      }
      navigate("/dashboard", { replace: true });
    } catch (e) {
      const detail = e?.response?.data?.detail || "Could not create your account";
      setErr(detail);
    } finally {
      setSubmitting(false);
    }
  };

  const onGoogle = () => {
    // Phase 36 Batch S — Emergent-managed Google Sign-In flow.
    // The referral / share_token can't traverse the Emergent redirect
    // hop, so we stash it in sessionStorage; AuthCallback reads it
    // back when the user lands after the OAuth round-trip.
    if (shareToken) {
      try { sessionStorage.setItem("runidea_share_token", shareToken); } catch (e) {
        // Phase 36 Batch 1 — log instead of silent swallow. The Google
        // OAuth hop continues regardless; referral attribution just won't
        // survive the redirect if sessionStorage is unavailable.
        console.warn("[Signup] stash share_token for Google hop failed:", e);
      }
    }
    const redirect = encodeURIComponent(`${window.location.origin}/auth/callback`);
    window.location.href = `https://auth.emergentagent.com/?redirect=${redirect}`;
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 px-4 py-12">
      <div className="w-full max-w-md">
        <Link to="/" className="mb-8 flex items-center justify-center gap-2" data-testid="back-to-home">
          <img src="/logo.png" alt="runidea.ai" className="h-9 w-9 rounded-md object-cover" />
          <span className="text-base font-semibold tracking-tight text-slate-900">
            runidea.ai
          </span>
        </Link>

        <div className="rounded-2xl border border-slate-200 bg-white p-8 shadow-sm">
          <h1 className="text-2xl font-bold tracking-tight text-slate-900" data-testid="signup-title">
            Create your account
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            Free forever. No credit card required.
          </p>

          <Button
            type="button"
            variant="outline"
            onClick={onGoogle}
            data-testid="google-signup-button"
            className="mt-6 w-full justify-center gap-2 border-slate-200 bg-white text-slate-700 transition-all hover:-translate-y-0.5 hover:border-slate-300 hover:bg-white hover:shadow-sm"
          >
            <svg className="h-4 w-4" viewBox="0 0 24 24" aria-hidden="true">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.99.66-2.25 1.06-3.71 1.06-2.85 0-5.27-1.93-6.13-4.52H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
              <path fill="#FBBC05" d="M5.87 14.11c-.22-.66-.35-1.36-.35-2.11s.13-1.45.35-2.11V7.05H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.95l3.69-2.84z" />
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.05l3.69 2.84C6.73 7.31 9.15 5.38 12 5.38z" />
            </svg>
            Sign up with Google
          </Button>

          <div className="my-6 flex items-center gap-3">
            <div className="h-px flex-1 bg-slate-200" />
            <span className="text-xs font-medium uppercase tracking-wider text-slate-400">or</span>
            <div className="h-px flex-1 bg-slate-200" />
          </div>

          <form onSubmit={onSubmit} className="space-y-4" noValidate>
            <div>
              <Label htmlFor="name" className="text-slate-700">Full name</Label>
              <Input
                id="name"
                data-testid="signup-name-input"
                type="text"
                autoComplete="name"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Jane Investor"
                className="mt-1.5"
              />
            </div>
            <div>
              <Label htmlFor="email" className="text-slate-700">Email</Label>
              <Input
                id="email"
                data-testid="signup-email-input"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                className="mt-1.5"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-slate-700">Password</Label>
              <Input
                id="password"
                data-testid="signup-password-input"
                type="password"
                autoComplete="new-password"
                required
                minLength={8}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="At least 8 characters"
                className="mt-1.5"
              />
            </div>

            {err && (
              <p
                role="alert"
                data-testid="signup-error"
                className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
              >
                {err}
              </p>
            )}

            <Button
              type="submit"
              disabled={submitting}
              data-testid="signup-submit-button"
              className="w-full bg-indigo-600 text-white transition-all hover:-translate-y-0.5 hover:bg-indigo-700 hover:shadow-md"
            >
              {submitting ? "Creating account…" : "Create account"}
            </Button>
          </form>

          <p className="mt-6 text-center text-sm text-slate-600">
            Already have an account?{" "}
            <Link
              to="/login"
              className="font-semibold text-indigo-600 hover:underline"
              data-testid="link-to-login"
            >
              Log in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
